import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { adminAPI, surveyAPI } from '../services/api'
import 'bootstrap-icons/font/bootstrap-icons.css'

const Admin = () => {
  const { isSuperuser, userGroups, hasAdminAccess } = useAuth()
  // Direct group-based and user-based access control
  const isAdmin = isSuperuser || userGroups.includes('Admin')
  
  // Helper function to format role display names
  const formatRoleDisplayName = (roleName) => {
    switch(roleName) {
      case 'ReadOnlySurveyor':
        return 'Read Only Surveyor'
      case 'ReadOnlyClient':
        return 'Read Only Client'
      default:
        return roleName
    }
  }

  const [activeTab, setActiveTab] = useState('users')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  
  // Data states for each model
  const [users, setUsers] = useState([])
  const [groups, setGroups] = useState([])
  const [surveyors, setSurveyors] = useState([])
  const [surveyorGroups, setSurveyorGroups] = useState([])
  const [surveyorGroupMembers, setSurveyorGroupMembers] = useState([])
  const [clients, setClients] = useState([])
  const [countries, setCountries] = useState([])
  const [ports, setPorts] = useState([])
  const [surveyTypes, setSurveyTypes] = useState([])
  const [operations, setOperations] = useState([])
  const [companyInfo, setCompanyInfo] = useState([])
  const [bankInfo, setBankInfo] = useState([])
  const [surveys, setSurveys] = useState([])
  
  // Modal states
  const [showModal, setShowModal] = useState(false)
  const [modalMode, setModalMode] = useState('create') // 'create' or 'edit'
  const [currentItem, setCurrentItem] = useState(null)
  const [formData, setFormData] = useState({})
  const [modalType, setModalType] = useState('') // Track what type of modal to show

  useEffect(() => {
    if (!hasAdminAccess) {
      window.location.href = '/'
      return
    }
    // Add a small delay if we just logged in to allow cookies to propagate
    const loginTimestamp = localStorage.getItem('loginTimestamp')
    const recentLogin = loginTimestamp && (Date.now() - parseInt(loginTimestamp) < 10000) // 10 seconds
    
    if (recentLogin) {
      setTimeout(() => {
        loadData(activeTab)
      }, 2000)
    } else {
      loadData(activeTab)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, hasAdminAccess])

  const loadData = async (tab) => {
    setLoading(true)
    setError(null)
    
    try {
      switch(tab) {
        case 'users': {
          try {
            const usersRes = await adminAPI.getUsers()
            // Backend already filters out superusers - they are managed separately and not shown in admin panel
            const allUsers = usersRes.data?.data || usersRes.data || []
            // Additional frontend filter to ensure no superusers are shown (double-check)
            const filteredUsers = allUsers.filter(user => !user.is_superuser)
            setUsers(filteredUsers)
          } catch (userErr) {
            console.error('Error loading users:', userErr)
            setUsers([])
            throw userErr // Re-throw to be caught by outer catch
          }
          // Also load groups for user form - do this separately so groups load even if users fail
          try {
            // Add a small delay if we just logged in to ensure cookies are available
            const loginTimestamp = localStorage.getItem('loginTimestamp')
            const recentLogin = loginTimestamp && (Date.now() - parseInt(loginTimestamp) < 10000)
            
            if (recentLogin) {
              await new Promise(resolve => setTimeout(resolve, 2000))
            }
            
            const groupsRes = await adminAPI.getGroups()
            const groupsData = groupsRes.data?.data || groupsRes.data || []
            setGroups(groupsData)
          } catch (groupErr) {
            console.error('Error loading groups:', groupErr)
            console.error('Error response:', groupErr.response?.data)
            // Don't throw - allow users to be displayed even if groups fail
            setGroups([])
            // Set error but don't block the UI - retry after a delay
            if (groupErr.response?.status === 403) {
              // Retry after 3 seconds - cookies might still be propagating
              setTimeout(async () => {
                try {
                  const retryRes = await adminAPI.getGroups()
                  const groupsData = retryRes.data?.data || retryRes.data || []
                  setGroups(groupsData)
                  console.log('Successfully loaded groups on retry:', groupsData)
                  setError(null) // Clear error on success
                } catch (retryErr) {
                  console.error('Retry failed:', retryErr)
                  setError('Authentication failed. Please refresh the page and try again.')
                }
              }, 3000)
            }
          }
          break
        }
        case 'surveyors': {
          const surveyorsRes = await adminAPI.getSurveyors()
          console.log('Surveyors API response:', surveyorsRes)
          console.log('Surveyors data:', surveyorsRes.data.data || surveyorsRes.data)
          const surveyorsData = surveyorsRes.data.data || surveyorsRes.data || []
          console.log('Setting surveyors state with:', surveyorsData)
          setSurveyors(surveyorsData)
          // Also load surveyor groups and members for the enhanced interface
          const groupsRes = await adminAPI.getSurveyorGroups()
          setSurveyorGroups(groupsRes.data.data || [])
          const membersRes = await adminAPI.getSurveyorGroupMembers()
          setSurveyorGroupMembers(membersRes.data.data || [])
          break
        }
        case 'clients': {
          const clientsRes = await adminAPI.getClients()
          setClients(clientsRes.data.data || [])
          break
        }
        case 'countries': {
          const countriesRes = await adminAPI.getCountries()
          setCountries(countriesRes.data.data || [])
          break
        }
        case 'ports': {
          const portsRes = await adminAPI.getPorts()
          setPorts(portsRes.data.data || [])
          // Also load countries for port form
          const countriesForPorts = await adminAPI.getCountries()
          setCountries(countriesForPorts.data.data || [])
          break
        }
        case 'survey-types': {
          const typesRes = await adminAPI.getSurveyTypes()
          setSurveyTypes(typesRes.data.data || [])
          break
        }
        case 'operations': {
          const opsRes = await adminAPI.getOperations()
          setOperations(opsRes.data.data || [])
          break
        }
        case 'company-info': {
          const companyRes = await adminAPI.getCompanyInfo()
          const companies = companyRes.data.data || []
          setCompanyInfo(companies)
          console.log('Loaded company info:', companies)
          break
        }
        case 'bank-info': {
          const bankRes = await adminAPI.getBankInfo()
          const banks = bankRes.data.data || []
          setBankInfo(banks)
          console.log('Loaded bank info:', banks)
          break
        }
        case 'surveys': {
          try {
            // Fetch all surveys - get all pages
            let allSurveys = []
            let page = 1
            let hasMore = true
            
            while (hasMore) {
              const surveysRes = await surveyAPI.list({ page })
              
              // Handle different response structures
              const surveysData = surveysRes.data?.results || surveysRes.data || []
              
              if (Array.isArray(surveysData)) {
                allSurveys = [...allSurveys, ...surveysData]
              }
              
              // Check if there are more pages
              const totalPages = surveysRes.data?.total_pages || 1
              const currentPage = surveysRes.data?.page || page
              hasMore = currentPage < totalPages && surveysData.length > 0
              
              page++
              
              // Safety limit
              if (page > 100) {
                console.warn('Reached page limit (100), stopping pagination')
                break
              }
            }
            
            setSurveys(allSurveys)
            console.log(`Loaded ${allSurveys.length} surveys`)
            
            if (allSurveys.length === 0) {
              console.log('No surveys found in database')
            }
          } catch (err) {
            console.error('Error loading surveys:', err)
            console.error('Error response:', err.response?.data)
            setError(`Failed to load surveys: ${err.response?.data?.message || err.message || 'Unknown error'}`)
            setSurveys([])
          }
          break
        }
        default:
          break
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to load data'
      setError(errorMessage)
      console.error('Error loading data:', err)
      console.error('Error response:', err.response?.data)
      // Don't redirect on 401/403 if we just logged in (cookies might still be propagating)
      const loginTimestamp = localStorage.getItem('loginTimestamp')
      const recentLogin = loginTimestamp && (Date.now() - parseInt(loginTimestamp) < 120000) // 2 minutes
      if ((err.response?.status === 401 || err.response?.status === 403) && recentLogin) {
        console.warn('API call failed after recent login, but not redirecting due to recent login')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    setModalMode('create')
    setCurrentItem(null)
    setFormData({})
    // Load groups if not already loaded (for users tab)
    if (activeTab === 'users') {
      if (groups.length === 0) {
        try {
          // Add a small delay to ensure cookies are available
          const loginTimestamp = localStorage.getItem('loginTimestamp')
          const recentLogin = loginTimestamp && (Date.now() - parseInt(loginTimestamp) < 10000)
          
          if (recentLogin) {
            await new Promise(resolve => setTimeout(resolve, 2000))
          }
          
          const groupsRes = await adminAPI.getGroups()
          const groupsData = groupsRes.data?.data || groupsRes.data || []
          setGroups(groupsData)
          console.log('Loaded groups for create modal:', groupsData)
        } catch (err) {
          console.error('Error loading groups:', err)
          console.error('Error details:', err.response?.data)
          const errorMsg = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to load groups'
          setError(`Failed to load groups: ${errorMsg}`)
          // Still show modal even if groups fail to load
        }
      }
      
      // Load surveyors if not already loaded (needed for ReadOnlySurveyor assignment)
      if (surveyors.length === 0) {
        try {
          const surveyorsRes = await adminAPI.getSurveyors()
          const surveyorsData = surveyorsRes.data?.data || surveyorsRes.data || []
          setSurveyors(surveyorsData)
          console.log('Loaded surveyors for user create modal:', surveyorsData)
        } catch (err) {
          console.error('Error loading surveyors:', err)
          console.error('Error details:', err.response?.data)
          const errorMsg = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to load surveyors'
          setError(`Failed to load surveyors: ${errorMsg}`)
          // Still show modal even if surveyors fail to load
        }
      }
      
      // Load clients if not already loaded (needed for ReadOnlyClient assignment)
      if (clients.length === 0) {
        try {
          const clientsRes = await adminAPI.getClients()
          const clientsData = clientsRes.data?.data || clientsRes.data || []
          setClients(clientsData)
          console.log('Loaded clients for user create modal:', clientsData)
        } catch (err) {
          console.error('Error loading clients:', err)
          console.error('Error details:', err.response?.data)
          const errorMsg = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to load clients'
          setError(`Failed to load clients: ${errorMsg}`)
          // Still show modal even if clients fail to load
        }
      }
    }
    setShowModal(true)
  }

  const handleEdit = async (item) => {
    
    // Set modal type based on item properties
    let modalTypeToSet = activeTab
    let updatedItem = item
    
    if (item.name && !item.code) {
      // It's a surveyor group (has name but no code)
      modalTypeToSet = 'surveyor-group'
    } else if (item.surveyor || (item.surveyor_name && item.group_name)) {
      // It's a surveyor group member (has surveyor or surveyor_name/group_name)
      modalTypeToSet = 'surveyor-group-member'
    } else if (item.client || (item.client_name && item.group_name)) {
      // It's a client group member (has client or client_name/group_name)
      modalTypeToSet = 'client-group-member'
    } else if (item.code) {
      // Check if it's a client or surveyor based on other fields
      if (item.email && !item.first_name && !item.last_name && !item.surveyor_type) {
        // It's a client (has email but no surveyor fields)
        modalTypeToSet = 'clients'
      } else {
        // It's a surveyor (has code and surveyor fields)
        modalTypeToSet = 'surveyors'
      }
    } else {
      // Default to active tab
      console.log('Defaulting to activeTab:', activeTab)
      modalTypeToSet = activeTab
    }
    
    console.log('Final updatedItem:', updatedItem)
    console.log('Final modalTypeToSet:', modalTypeToSet)
    
    // Set all state at once
    setModalMode('edit')
    setModalType(modalTypeToSet)
    setCurrentItem(updatedItem)
    
    // Handle country field for ports - convert object to ID if needed
    const formDataCopy = { ...updatedItem }
    if (activeTab === 'ports' && formDataCopy.country && typeof formDataCopy.country === 'object') {
      formDataCopy.country = formDataCopy.country.id
    }
    // Handle groups for users - ensure groups are loaded first
    if (activeTab === 'users') {
      // Load groups if not already loaded
      if (groups.length === 0) {
        try {
          const groupsRes = await adminAPI.getGroups()
          const groupsData = groupsRes.data?.data || groupsRes.data || []
          setGroups(groupsData)
          console.log('Loaded groups for edit modal:', groupsData)
        } catch (err) {
          console.error('Error loading groups:', err)
          console.error('Error details:', err.response?.data)
          const errorMsg = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to load groups'
          setError(`Failed to load groups: ${errorMsg}`)
          // Retry after a delay if 403
          if (err.response?.status === 403) {
            setTimeout(async () => {
              try {
                const retryRes = await adminAPI.getGroups()
                const groupsData = retryRes.data?.data || retryRes.data || []
                setGroups(groupsData)
                console.log('Successfully loaded groups on retry:', groupsData)
                setError(null)
              } catch (retryErr) {
                console.error('Retry failed:', retryErr)
              }
            }, 1000)
          }
        }
      }
      // Load surveyors if not already loaded (needed for ReadOnlySurveyor assignment)
      if (surveyors.length === 0) {
        try {
          const surveyorsRes = await adminAPI.getSurveyors()
          const surveyorsData = surveyorsRes.data?.data || surveyorsRes.data || []
          setSurveyors(surveyorsData)
          console.log('Loaded surveyors for user modal:', surveyorsData)
        } catch (err) {
          console.error('Error loading surveyors:', err)
          console.error('Error details:', err.response?.data)
          const errorMsg = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to load surveyors'
          setError(`Failed to load surveyors: ${errorMsg}`)
          // Still show modal even if surveyors fail to load
        }
      }
      
      // Load clients if not already loaded (needed for ReadOnlyClient assignment)
      if (clients.length === 0) {
        try {
          const clientsRes = await adminAPI.getClients()
          const clientsData = clientsRes.data?.data || clientsRes.data || []
          setClients(clientsData)
          console.log('Loaded clients for user modal:', clientsData)
        } catch (err) {
          console.error('Error loading clients:', err)
          console.error('Error details:', err.response?.data)
          const errorMsg = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to load clients'
          setError(`Failed to load clients: ${errorMsg}`)
          // Still show modal even if clients fail to load
        }
      }
      // Convert group names to IDs (single selection)
      if (formDataCopy.groups) {
        // Take only the first group (single selection)
        const firstGroupName = Array.isArray(formDataCopy.groups) ? formDataCopy.groups[0] : formDataCopy.groups
        const group = groups.find(g => g.name === firstGroupName)
        formDataCopy.group_ids = group ? [group.id] : []
      }
      
      // Handle assigned_clients for ReadOnlyClient users
      // Prioritize assigned_clients (new field) over assigned_client (deprecated field)
      if (formDataCopy.assigned_clients && formDataCopy.assigned_clients.length > 0) {
        // assigned_clients already exists and has data, keep it as is
      } else if (formDataCopy.assigned_client) {
        // Convert single assigned_client to assigned_clients array for backward compatibility
        formDataCopy.assigned_clients = Array.isArray(formDataCopy.assigned_client) 
          ? formDataCopy.assigned_client 
          : [formDataCopy.assigned_client]
      } else {
        // Initialize empty array
        formDataCopy.assigned_clients = []
      }
    }
    // Handle company-info - keep fixed fields as they are
    if (activeTab === 'company-info') {
      // No conversion needed - keep fields as they are
    }
    // Handle bank-info - ensure custom_fields exists
    if (activeTab === 'bank-info') {
      if (!formDataCopy.custom_fields) {
        formDataCopy.custom_fields = {}
      }
    }
    setFormData(formDataCopy)
    setShowModal(true)
  }

  const handleDelete = async (id, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return
    
    try {
      switch(type) {
        case 'user':
          await adminAPI.deleteUser(id)
          break
        case 'surveyor':
          await adminAPI.deleteSurveyor(id)
          break
        case 'surveyor-group':
          await adminAPI.deleteSurveyorGroup(id)
          break
        case 'surveyor-group-member':
          await adminAPI.deleteSurveyorGroupMember(id)
          break
        case 'client':
          await adminAPI.deleteClient(id)
          break
        case 'country':
          await adminAPI.deleteCountry(id)
          break
        case 'port':
          await adminAPI.deletePort(id)
          break
        case 'survey-type':
          await adminAPI.deleteSurveyType(id)
          break
        case 'operation':
          await adminAPI.deleteOperation(id)
          break
        case 'company-info':
          await adminAPI.deleteCompanyInfo(id)
          break
        case 'bank-info':
          await adminAPI.deleteBankInfo(id)
          break
        case 'survey':
          await surveyAPI.delete(id)
          break
        default:
          break
      }
      // Reload data after successful delete
      await loadData(activeTab)
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.detail || 
                          err.message || 
                          `Failed to delete ${type}`
      alert(errorMessage)
      console.error('Delete error:', err)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    
    console.log('handleSubmit called with modalType:', modalType)
    console.log('handleSubmit called with currentItem:', currentItem)
    
    try {
      let saveType = modalType || activeTab
      
      // Validation for ReadOnlyClient users
      if (saveType === 'users') {
        const selectedGroupId = formData.group_ids && formData.group_ids.length > 0 ? formData.group_ids[0] : null
        const selectedGroup = selectedGroupId ? groups.find(g => g.id === selectedGroupId) : null
        
        if (selectedGroup && selectedGroup.name === 'ReadOnlyClient') {
          if (!formData.assigned_clients || formData.assigned_clients.length === 0) {
            throw new Error('At least one client must be assigned to ReadOnlyClient users')
          }
        }
        
        if (selectedGroup && selectedGroup.name === 'ReadOnlySurveyor') {
          if (!formData.assigned_surveyor) {
            throw new Error('A surveyor must be assigned to ReadOnlySurveyor users')
          }
        }
      }
      
      switch(saveType) {
        case 'users':
          if (modalMode === 'create') {
            await adminAPI.createUser(formData)
          } else {
            // For updates, only send fields that were actually changed
            const updateData = {}
            
            // Always send group_ids if present
            if (formData.group_ids) {
              updateData.group_ids = formData.group_ids
            }
            
            // Only send welcome_name if it's different from the original
            if (formData.welcome_name !== currentItem.welcome_name) {
              updateData.welcome_name = formData.welcome_name
            }
            
            // Only send assigned_surveyor if it's different
            if (formData.assigned_surveyor !== currentItem.assigned_surveyor) {
              updateData.assigned_surveyor = formData.assigned_surveyor
            }
            
            // Only send assigned_clients if it's different
            const currentAssignedClients = (currentItem.assigned_clients || [])
            const newAssignedClients = (formData.assigned_clients || [])
            
            // Compare arrays properly
            const clientsChanged = 
                currentAssignedClients.length !== newAssignedClients.length ||
                currentAssignedClients.some(client => !newAssignedClients.includes(client)) ||
                newAssignedClients.some(client => !currentAssignedClients.includes(client))
            
            if (clientsChanged) {
              updateData.assigned_clients = newAssignedClients
            }
            
            console.log('Update data being sent:', updateData)
            await adminAPI.updateUser(currentItem.id, updateData)
          }
          break
        case 'surveyors':
          console.log('Surveyor case - modalMode:', modalMode)
          console.log('Surveyor case - currentItem:', currentItem)
          if (modalMode === 'create') {
            await adminAPI.createSurveyor(formData)
          } else {
            console.log('Calling updateSurveyor with code:', currentItem.code)
            await adminAPI.updateSurveyor(currentItem.code, formData)
          }
          break
        case 'surveyor-group':
          if (modalMode === 'create') {
            await adminAPI.createSurveyorGroup(formData)
          } else {
            await adminAPI.updateSurveyorGroup(currentItem.id, formData)
          }
          break
        case 'surveyor-group-member':
          if (modalMode === 'create') {
            await adminAPI.createSurveyorGroupMember(formData)
          } else {
            await adminAPI.updateSurveyorGroupMember(currentItem.id, formData)
          }
          break
        case 'clients':
          if (modalMode === 'create') {
            await adminAPI.createClient(formData)
          } else {
            await adminAPI.updateClient(currentItem.code, formData)
          }
          break
        case 'countries':
          if (modalMode === 'create') {
            await adminAPI.createCountry(formData)
          } else {
            await adminAPI.updateCountry(currentItem.id, formData)
          }
          break
        case 'ports':
          if (modalMode === 'create') {
            await adminAPI.createPort(formData)
          } else {
            await adminAPI.updatePort(currentItem.id, formData)
          }
          break
        case 'survey-types':
          if (modalMode === 'create') {
            await adminAPI.createSurveyType(formData)
          } else {
            await adminAPI.updateSurveyType(currentItem.name, formData)
          }
          break
        case 'operations':
          if (modalMode === 'create') {
            await adminAPI.createOperation(formData)
          } else {
            await adminAPI.updateOperation(currentItem.id, formData)
          }
          break
        case 'company-info':
          // Use fixed fields directly
          const companyInfoData = {
            company_name: formData.company_name,
            company_address: formData.company_address || '',
            company_tel: formData.company_tel || '',
            company_email: formData.company_email || '',
            company_web: formData.company_web || ''
          }
          
          if (modalMode === 'create') {
            await adminAPI.createCompanyInfo(companyInfoData)
          } else {
            await adminAPI.updateCompanyInfo(currentItem.id, companyInfoData)
          }
          break
        case 'bank-info':
          // Use fixed fields instead of custom_fields
          const bankInfoData = {
            name: formData.name,
            bank_name: formData.bank_name || '',
            bank_address: formData.bank_address || '',
            account_holder: formData.account_holder || '',
            swift: formData.swift || '',
            bank_branch_code: formData.bank_branch_code || '',
            account_number_sgd: formData.account_number_sgd || '',
            account_number_usd: formData.account_number_usd || ''
          }
          if (modalMode === 'create') {
            await adminAPI.createBankInfo(bankInfoData)
          } else {
            await adminAPI.updateBankInfo(currentItem.id, bankInfoData)
          }
          break
        default:
          break
      }
      
      setShowModal(false)
      setModalType('')
      await loadData(activeTab)
    } catch (err) {
      // Format error message better
      let errorMessage = 'Failed to save'
      if (err.response?.data?.errors) {
        // Handle validation errors - format them nicely
        const errors = err.response.data.errors
        if (typeof errors === 'object' && !Array.isArray(errors)) {
          const errorMessages = Object.entries(errors).map(([key, value]) => {
            if (Array.isArray(value)) {
              return `${key}: ${value.join(', ')}`
            }
            return `${key}: ${value}`
          })
          errorMessage = errorMessages.join('\n')
        } else {
          errorMessage = JSON.stringify(errors)
        }
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err.message) {
        errorMessage = err.message
      }
      
      setError(errorMessage)
      console.error('Error saving:', err)
    } finally {
      setLoading(false)
    }
  }

  const tabs = [
    { id: 'users', label: 'Users', icon: 'bi-people' },
    { id: 'surveyors', label: 'Surveyors', icon: 'bi-person-badge' },
    { id: 'clients', label: 'Clients', icon: 'bi-building' },
    { id: 'countries', label: 'Countries', icon: 'bi-globe' },
    { id: 'ports', label: 'Ports', icon: 'bi-water' },
    { id: 'survey-types', label: 'Survey Types', icon: 'bi-clipboard-data' },
    { id: 'operations', label: 'Operations', icon: 'bi-calendar-event' },
    { id: 'company-info', label: 'Company Info', icon: 'bi-building-gear' },
    { id: 'bank-info', label: 'Bank Info', icon: 'bi-credit-card' },
    { id: 'surveys', label: 'Surveys', icon: 'bi-clipboard-check' }
  ]

  const renderTable = () => {
    const dataMap = {
      users,
      surveyors,
      clients,
      countries,
      ports,
      'survey-types': surveyTypes,
      operations,
      'company-info': companyInfo,
      'bank-info': bankInfo,
      surveys
    }
    
    const data = (dataMap[activeTab] || [])
    // Ensure data is always an array
    const dataArray = Array.isArray(data) ? data : []

    switch(activeTab) {
      case 'users':
        return (
          <table className="w-full">
            <thead className="table-head-premium">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Username</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Groups</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Welcome Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Assigned</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dataArray.map(user => (
                <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{user.username}</td>
                  <td className="px-4 py-3 text-sm">{user.email || '-'}</td>
                  <td className="px-4 py-3 text-sm">{`${user.first_name || ''} ${user.last_name || ''}`.trim() || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    {user.groups && user.groups.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {user.groups.map((group, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                            {formatRoleDisplayName(group)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">No groups</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">{user.welcome_name || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    {user.groups && user.groups.includes('ReadOnlySurveyor') && user.assigned_surveyor && (
                      <div className="text-xs">
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs font-semibold rounded">
                          Surveyor: {user.assigned_surveyor}
                        </span>
                      </div>
                    )}
                    {user.groups && user.groups.includes('ReadOnlyClient') && (
                      <div className="text-xs">
                        {user.assigned_clients && user.assigned_clients.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {user.assigned_clients.map((client, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                                {client}
                              </span>
                            ))}
                          </div>
                        ) : user.assigned_client ? (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                            {user.assigned_client}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">No clients</span>
                        )}
                      </div>
                    )}
                    {(!user.groups || !user.groups.some(g => g.includes('ReadOnly'))) && (
                      <span className="text-gray-400 text-xs">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button onClick={() => handleEdit(user)} className="text-blue-600 hover:text-blue-800 mr-3">
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button onClick={() => handleDelete(user.id, 'user')} className="text-red-600 hover:text-red-800">
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      case 'surveyors':
        return (
          <div className="space-y-8">
            {/* Surveyor Groups Cards */}
            <div>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <i className="bi bi-people-fill text-purple-600"></i>
                  Surveyor Groups
                </h3>
                <button
                  onClick={() => {
                    setModalMode('create')
                    setCurrentItem(null)
                    setModalType('surveyor-group')
                    setFormData({ name: '', description: '', is_active: true })
                    setShowModal(true)
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl"
                >
                  <i className="bi bi-people-fill"></i>
                  Create Surveyor Group
                </button>
              </div>
              {surveyorGroups.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {surveyorGroups.map(group => (
                    <div key={group.id} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                      <div className={`h-2 ${group.is_active ? 'bg-gradient-to-r from-purple-400 to-purple-600' : 'bg-gradient-to-r from-gray-400 to-gray-600'}`}></div>
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <h4 className="text-lg font-bold text-gray-900 mb-2">{group.name}</h4>
                            <p className="text-sm text-gray-600 line-clamp-2">{group.description || 'No description'}</p>
                          </div>
                          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${group.is_active ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                            {group.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <i className="bi bi-people text-purple-500"></i>
                            <span>{group.member_count || 0} members</span>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(group)}
                              className="p-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors"
                              title="Edit Group"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              onClick={() => handleDelete(group.id, 'surveyor-group')}
                              className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                              title="Delete Group"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                  <i className="bi bi-people text-6xl text-gray-400 mb-4"></i>
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">No Surveyor Groups Yet</h3>
                  <p className="text-gray-500">Create your first surveyor group to organize your surveyors</p>
                </div>
              )}
            </div>

            {/* Surveyors Cards */}
            <div>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <i className="bi bi-person-badge-fill text-blue-600"></i>
                  Surveyors
                </h3>
                <button
                  onClick={() => {
                    setModalMode('create')
                    setCurrentItem(null)
                    setModalType('surveyors')
                    setFormData({ code: '', first_name: '', last_name: '', email: '', phone: '', surveyor_type: 'In-House', is_active: true })
                    setShowModal(true)
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl"
                >
                  <i className="bi bi-person-plus-fill"></i>
                  Add Surveyor
                </button>
              </div>
              {surveyors.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {surveyors.map(surveyor => {
                    // Get groups for this surveyor
                    const surveyorGroupsList = surveyorGroupMembers
                      .filter(member => member.surveyor === surveyor.code)
                      .map(member => {
                        const group = surveyorGroups.find(g => g.id === member.group)
                        return group ? group.name : 'Unknown'
                      })

                    return (
                      <div key={surveyor.code} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                        <div className={`h-2 ${surveyor.is_active ? 'bg-gradient-to-r from-blue-400 to-blue-600' : 'bg-gradient-to-r from-gray-400 to-gray-600'}`}></div>
                        <div className="p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h4 className="text-lg font-bold text-gray-900 mb-1">{surveyor.code}</h4>
                              <p className="text-sm text-gray-600">
                                {`${surveyor.first_name || ''} ${surveyor.last_name || ''}`.trim() || '-'}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEdit(surveyor)}
                                className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                title="Edit Surveyor"
                              >
                                <i className="bi bi-pencil text-sm"></i>
                              </button>
                              <button
                                onClick={() => handleDelete(surveyor.code, 'surveyor')}
                                className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                                title="Delete Surveyor"
                              >
                                <i className="bi bi-trash text-sm"></i>
                              </button>
                            </div>
                          </div>
                          
                          <div className="space-y-3 mb-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <i className="bi bi-envelope text-blue-500"></i>
                              <span className="truncate">{surveyor.email || 'No email'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <i className="bi bi-telephone text-green-500"></i>
                              <span>{surveyor.phone || 'No phone'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <i className="bi bi-briefcase text-purple-500"></i>
                              <span>{surveyor.surveyor_type || '-'}</span>
                            </div>
                          </div>

                          <div className="mb-4">
                            <p className="text-xs font-semibold text-gray-700 mb-2">Groups:</p>
                            <div className="flex justify-between items-center">
                              <div className="flex flex-wrap gap-1">
                                {surveyorGroupsList.length > 0 ? (
                                  surveyorGroupsList.map((groupName, idx) => (
                                    <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded-full">
                                      {groupName}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs text-gray-400 italic">No groups</span>
                                )}
                              </div>
                              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${surveyor.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                {surveyor.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                  <i className="bi bi-person-badge text-6xl text-gray-400 mb-4"></i>
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">No Surveyors Yet</h3>
                  <p className="text-gray-500">Add your first surveyor to get started</p>
                  <button
                    onClick={() => {
                      setModalMode('create')
                      setCurrentItem(null)
                      setFormData({ code: '', first_name: '', last_name: '', email: '', phone: '', surveyor_type: 'In-House', is_active: true })
                      setShowModal(true)
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 flex items-center gap-2 mx-auto"
                  >
                    <i className="bi bi-person-plus-fill"></i>
                    Add First Surveyor
                  </button>
                </div>
              )}
            </div>
          </div>
        )
      case 'clients':
        return (
          <table className="w-full">
            <thead className="table-head-premium">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap min-w-[8.5rem]">Client Code</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Address</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dataArray.map(client => (
                <tr key={client.code} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{client.name}</td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">{client.code || '-'}</td>
                  <td className="px-4 py-3 text-sm">{client.email || '-'}</td>
                  <td className="px-4 py-3 text-sm">{client.address || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <button onClick={() => handleEdit(client)} className="text-blue-600 hover:text-blue-800 mr-3">
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button onClick={() => handleDelete(client.code, 'client')} className="text-red-600 hover:text-red-800">
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      case 'countries':
        return (
          <table className="w-full">
            <thead className="table-head-premium">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dataArray.map(country => (
                <tr key={country.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{country.name}</td>
                  <td className="px-4 py-3 text-sm">
                    <button onClick={() => handleEdit(country)} className="text-blue-600 hover:text-blue-800 mr-3">
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button onClick={() => handleDelete(country.id, 'country')} className="text-red-600 hover:text-red-800">
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      case 'ports':
        return (
          <table className="w-full">
            <thead className="table-head-premium">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Country</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dataArray.map(port => (
                <tr key={port.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{port.name}</td>
                  <td className="px-4 py-3 text-sm">{port.country_name || (typeof port.country === 'object' ? port.country?.name : port.country) || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <button onClick={() => handleEdit(port)} className="text-blue-600 hover:text-blue-800 mr-3">
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button onClick={() => handleDelete(port.id, 'port')} className="text-red-600 hover:text-red-800">
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      case 'survey-types':
        return (
          <table className="w-full">
            <thead className="table-head-premium">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dataArray.map(type => (
                <tr key={type.name} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{type.name}</td>
                  <td className="px-4 py-3 text-sm">
                    <button onClick={() => handleEdit(type)} className="text-blue-600 hover:text-blue-800 mr-3">
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button onClick={() => handleDelete(type.name, 'survey-type')} className="text-red-600 hover:text-red-800">
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      case 'operations':
        return (
          <table className="w-full">
            <thead className="table-head-premium">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Title</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Start Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">End Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Survey Ref</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dataArray.map(op => (
                <tr key={op.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{op.title}</td>
                  <td className="px-4 py-3 text-sm">{op.start_date}</td>
                  <td className="px-4 py-3 text-sm">{op.end_date}</td>
                  <td className="px-4 py-3 text-sm">{op.survey_ref || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <button onClick={() => handleEdit(op)} className="text-blue-600 hover:text-blue-800 mr-3">
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button onClick={() => handleDelete(op.id, 'operation')} className="text-red-600 hover:text-red-800">
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      case 'surveys':
        if (!data || data.length === 0) {
          return (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <i className="bi bi-file-earmark-text text-6xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-600 mb-2">No Surveys Found</h3>
              <p className="text-sm text-gray-500">There are no surveys in the system yet.</p>
            </div>
          )
        }
        return (
          <table className="w-full">
            <thead className="table-head-premium">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Reference Number</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Client</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Vessel</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Survey Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Port</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">ETA</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dataArray.map(survey => (
                <tr key={survey.reference_number} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{survey.reference_number}</td>
                  <td className="px-4 py-3 text-sm">{survey.client_name || '-'}</td>
                  <td className="px-4 py-3 text-sm">{survey.vessel_name || '-'}</td>
                  <td className="px-4 py-3 text-sm">{survey.survey_type || '-'}</td>
                  <td className="px-4 py-3 text-sm">{survey.survey_port || '-'}</td>
                  <td className="px-4 py-3 text-sm">{survey.eta ? (() => {
                    const [year, month, day] = survey.eta.split('-').map(Number)
                    const date = new Date(year, month - 1, day) // Local date constructor
                    return date.toLocaleDateString('en-GB')
                  })() : '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    {survey.cancelled ? (
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded">Cancelled</span>
                    ) : (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button 
                      onClick={() => handleDelete(survey.reference_number, 'survey')} 
                      className="text-red-600 hover:text-red-800"
                      title="Delete Survey"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      case 'company-info':
        if (!data || data.length === 0) {
          return (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <i className="bi bi-building text-6xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-600 mb-2">No Company Info</h3>
              <p className="text-sm text-gray-500">Create your first company info to get started</p>
            </div>
          )
        }
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {dataArray.map(company => (
              <div key={company.id} className="bg-white rounded-xl border border-[#d9e2f1] shadow-[0_1px_6px_rgba(1,109,247,0.12)] overflow-hidden">
                <div className="table-head-premium px-4 py-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-white truncate">{company.company_name}</h3>
                    <div className="flex gap-1">
                      <button 
                        onClick={() => handleEdit(company)} 
                        className="h-7 w-7 inline-flex items-center justify-center text-sm bg-white/15 hover:bg-white/30 text-white rounded-md transition-colors"
                        title="Edit"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button 
                        onClick={() => handleDelete(company.id, 'company-info')} 
                        className="h-7 w-7 inline-flex items-center justify-center text-sm bg-white/15 hover:bg-white/30 text-white rounded-md transition-colors"
                        title="Delete"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="p-4 space-y-2.5">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-0.5">Address</div>
                    <div className="text-sm text-gray-800 whitespace-pre-line">{company.company_address || '-'}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-0.5">Telephone</div>
                    <div className="text-sm text-gray-800">{company.company_tel || '-'}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-0.5">Email</div>
                    <div className="text-sm text-gray-800">{company.company_email || '-'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      case 'bank-info':
        if (!data || data.length === 0) {
          return (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <i className="bi bi-credit-card text-6xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-600 mb-2">No Bank Info</h3>
              <p className="text-sm text-gray-500">Create your first bank info to get started</p>
            </div>
          )
        }
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {dataArray.map(bank => (
              <div key={bank.id} className="bg-white rounded-xl border border-[#d9e2f1] shadow-[0_1px_6px_rgba(1,109,247,0.12)] overflow-hidden">
                  <div className="table-head-premium px-4 py-2">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-white truncate">{bank.name || 'Unnamed Bank'}</h3>
                      <div className="flex gap-1">
                        <button 
                          onClick={() => handleEdit(bank)} 
                          className="h-7 w-7 inline-flex items-center justify-center text-sm bg-white/15 hover:bg-white/30 text-white rounded-md transition-colors"
                          title="Edit"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button 
                          onClick={() => handleDelete(bank.id, 'bank-info')} 
                          className="h-7 w-7 inline-flex items-center justify-center text-sm bg-white/15 hover:bg-white/30 text-white rounded-md transition-colors"
                          title="Delete"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 space-y-2.5">
                    {/* Define the desired order of fields using fixed fields */}
                    {[
                      { key: 'Account Holder', value: bank.account_holder },
                      { key: 'Bank Name', value: bank.bank_name },
                      { key: 'Bank Address', value: bank.bank_address },
                      { key: 'SWIFT', value: bank.swift },
                      { key: 'Bank / Branch Code:', value: bank.bank_branch_code },
                      { key: 'Account Number (SGD)', value: bank.account_number_sgd },
                      { key: 'Account Number (USD)', value: bank.account_number_usd }
                    ]
                      .filter(item => item.value && item.value !== 'N/A' && item.value !== '-' && item.value.toString().trim() !== '')
                      .map(item => (
                        <div key={item.key}>
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-0.5">{item.key}</div>
                          <div className={`text-sm text-gray-800 ${item.key.includes('Number') || item.key.includes('SWIFT') || item.key.includes('IBAN') || item.key.includes('Code') ? 'font-mono' : ''}`}>
                            {item.value}
                          </div>
                        </div>
                      ))}
                    {bank.account_holder && bank.bank_name && bank.swift && bank.account_number_sgd && bank.account_number_usd ? (
                      ''
                    ) : (
                      <div className="text-sm text-gray-500 italic">No bank information added yet</div>
                    )}
                  </div>
                </div>
            ))}
          </div>
        )
      default:
        return <div>No data</div>
    }
  }

  const renderForm = () => {
    // Handle special cases for surveyors tab (groups and group members)
    if (activeTab === 'surveyors') {
      if (modalType === 'surveyor-group') {
        // It's a surveyor group
        return renderModalForm('surveyor-group')
      } else if (modalType === 'surveyor-group-member') {
        // It's a group member
        return renderModalForm('surveyor-group-member')
      } else {
        // It's a surveyor
        return renderModalForm('surveyors')
      }
    }
    
    // Handle other tabs normally
    return renderModalForm(activeTab)
  }

  const renderModalForm = (tab) => {
    switch(tab) {
      case 'users':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Username *</label>
              <input
                type="text"
                value={formData.username || ''}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                required
                disabled={modalMode === 'edit'}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">First Name</label>
              <input
                type="text"
                value={formData.first_name || ''}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Last Name</label>
              <input
                type="text"
                value={formData.last_name || ''}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Welcome Name</label>
              <input
                type="text"
                value={formData.welcome_name || ''}
                onChange={(e) => setFormData({...formData, welcome_name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Custom welcome name to display"
              />
              <p className="text-xs text-gray-500 mt-1">This name will be displayed in welcome messages instead of the user's first/last name</p>
            </div>

            {/* Password Field */}
            {modalMode === 'create' && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Password *</label>
                <input
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required={modalMode === 'create'}
                />
              </div>
            )}
            {modalMode === 'edit' && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">New Password (leave blank to keep current)</label>
                <input
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            )}

            {/* Role Assignment - Hierarchical Card Selection (at bottom) */}
            <div className="mb-6 mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <i className="bi bi-shield-check text-blue-600"></i>
                Role Assignment *
              </h4>
              <p className="text-xs text-gray-600 mb-4">
                Select one role for this user. Roles are displayed in hierarchy order from highest to lowest permissions.
              </p>
              
              {/* Hierarchical Layout: Admin (top) -> Manager (middle) -> Survey Staff & ReadOnly (bottom) */}
              {groups.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500">Loading roles...</p>
                </div>
              ) : (
              <div className="flex flex-col items-center gap-4">
                {/* Helper function to get role info */}
                {(() => {
                  const getRoleInfo = (name) => {
                    switch(name) {
                      case 'Admin':
                        return {
                          icon: 'bi-person-badge',
                          color: 'from-red-500 to-red-600',
                          borderColor: 'border-red-300',
                          bgColor: 'bg-red-50',
                          textColor: 'text-red-700',
                          description: 'Manage users, assign roles'
                        }
                      case 'Manager':
                        return {
                          icon: 'bi-person-workspace',
                          color: 'from-blue-500 to-blue-600',
                          borderColor: 'border-blue-300',
                          bgColor: 'bg-blue-50',
                          textColor: 'text-blue-700',
                          description: 'Create/update surveys'
                        }
                      case 'Finance Manager':
                        return {
                          icon: 'bi-cash-stack',
                          color: 'from-emerald-500 to-emerald-600',
                          borderColor: 'border-emerald-300',
                          bgColor: 'bg-emerald-50',
                          textColor: 'text-emerald-700',
                          description: 'Full financial access, manage invoices'
                        }
                      case 'Staff':
                        return {
                          icon: 'bi-person-check',
                          color: 'from-green-500 to-green-600',
                          borderColor: 'border-green-300',
                          bgColor: 'bg-green-50',
                          textColor: 'text-green-700',
                          description: 'Submit reports'
                        }
                      case 'ReadOnlyClient':
                        return {
                          icon: 'bi-person',
                          color: 'from-gray-500 to-gray-600',
                          borderColor: 'border-gray-300',
                          bgColor: 'bg-gray-50',
                          textColor: 'text-gray-700',
                          description: 'Read-only access (Client)'
                        }
                      case 'ReadOnlySurveyor':
                        return {
                          icon: 'bi-person',
                          color: 'from-purple-500 to-purple-600',
                          borderColor: 'border-purple-300',
                          bgColor: 'bg-purple-50',
                          textColor: 'text-purple-700',
                          description: 'Read-only access (Surveyor)'
                        }
                      default:
                        return {
                          icon: 'bi-person',
                          color: 'from-gray-500 to-gray-600',
                          borderColor: 'border-gray-300',
                          bgColor: 'bg-gray-50',
                          textColor: 'text-gray-700',
                          description: 'User role'
                        }
                    }
                  }
                  
                  const sortedGroups = groups.slice().sort((a, b) => {
                    const order = { 'Admin': 1, 'Manager': 2, 'Finance Manager': 3, 'Staff': 4, 'ReadOnlySurveyor': 5, 'ReadOnlyClient': 6 }
                    return (order[a.name] || 99) - (order[b.name] || 99)
                  })

                  const adminGroup = sortedGroups.find(g => g.name === 'Admin')
                  const managerGroup = sortedGroups.find(g => g.name === 'Manager')
                  const financeManagerGroup = sortedGroups.find(g => g.name === 'Finance Manager')
                  const staffGroup = sortedGroups.find(g => g.name === 'Staff')
                  const readOnlySurveyorGroup = sortedGroups.find(g => g.name === 'ReadOnlySurveyor')
                  const readOnlyClientGroup = sortedGroups.find(g => g.name === 'ReadOnlyClient')
                  
                  const renderRoleCard = (group) => {
                    if (!group) return null
                    const isSelected = formData.group_ids && formData.group_ids.includes(group.id)
                    const roleInfo = getRoleInfo(group.name)
                    
                    return (
                      <button
                        key={group.id}
                        type="button"
                        onClick={() => {
                          setFormData({...formData, group_ids: [group.id]})
                        }}
                        className={`relative p-5 rounded-xl border-2 transition-all duration-200 text-left w-full ${
                          isSelected
                            ? `${roleInfo.borderColor} ${roleInfo.bgColor} shadow-xl scale-105 ring-2 ring-offset-2 ${roleInfo.borderColor.replace('border-', 'ring-')}`
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-lg hover:scale-[1.02]'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br ${roleInfo.color} flex items-center justify-center text-white text-lg`}>
                            <i className={`bi ${roleInfo.icon}`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className={`font-semibold text-sm mb-1 ${isSelected ? roleInfo.textColor : 'text-gray-900'} leading-tight`}>
                              {formatRoleDisplayName(group.name)}
                            </h5>
                            <p className={`text-xs ${isSelected ? roleInfo.textColor : 'text-gray-600'} leading-tight`}>
                              {roleInfo.description}
                            </p>
                          </div>
                          {isSelected && (
                            <div className="flex-shrink-0">
                              <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${roleInfo.color} flex items-center justify-center`}>
                                <i className="bi bi-check text-white text-sm"></i>
                              </div>
                            </div>
                          )}
                        </div>
                      </button>
                    )
                  }
                  
                  return (
                    <>
                      {/* Professional 1-1-3 Hierarchy Layout */}
                      <div className="flex flex-col items-center gap-8 w-full py-4">
                        {/* Level 1: Admin (Top - Single Card) */}
                        {adminGroup && (
                          <div className="flex flex-col items-center">
                            <div className="relative">
                              {renderRoleCard(adminGroup)}
                              {/* Badge indicator for top level */}
                              <div className="absolute -top-2 -right-2 bg-gradient-to-br from-yellow-400 to-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-md">
                                Level 1
                              </div>
                            </div>
                            {(managerGroup || financeManagerGroup) && (
                              <div className="mt-6 flex items-center justify-center">
                                <div className="h-10 w-1 bg-gradient-to-b from-gray-400 via-gray-300 to-gray-200 rounded-full shadow-sm"></div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Level 2: Finance Manager and Manager (Middle - Two Cards) */}
                        {(financeManagerGroup || managerGroup) && (
                          <div className="flex flex-col items-center w-full">
                            <div className="flex flex-col md:flex-row items-stretch justify-center gap-4">
                              {financeManagerGroup && (
                                <div className="relative">
                                  {renderRoleCard(financeManagerGroup)}
                                  {/* Badge indicator for middle level */}
                                  <div className="absolute -top-2 -right-2 bg-gradient-to-br from-emerald-400 to-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-md">
                                    Level 2
                                  </div>
                                </div>
                              )}
                              {managerGroup && (
                                <div className="relative">
                                  {renderRoleCard(managerGroup)}
                                  {/* Badge indicator for middle level */}
                                  <div className="absolute -top-2 -right-2 bg-gradient-to-br from-blue-400 to-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-md">
                                    Level 2
                                  </div>
                                </div>
                              )}
                            </div>
                            {(staffGroup || readOnlySurveyorGroup || readOnlyClientGroup) && (
                              <div className="mt-6 flex items-center justify-center">
                                <div className="h-10 w-1 bg-gradient-to-b from-gray-400 via-gray-300 to-gray-200 rounded-full shadow-sm"></div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Level 3: Staff, ReadOnlySurveyor, ReadOnlyClient (Bottom - Three Cards) */}
                        {(staffGroup || readOnlySurveyorGroup || readOnlyClientGroup) && (
                          <div className="w-full max-w-7xl px-4">
                            <div className="relative">
                              {/* Level 3 Badge */}
                              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-br from-purple-400 to-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                                Level 3
                              </div>
                              <div className="flex flex-col md:flex-row items-stretch justify-center gap-4 pt-2">
                                {staffGroup && (
                                  <div className="flex-1 flex justify-center min-w-[200px] max-w-[280px]">
                                    {renderRoleCard(staffGroup)}
                                  </div>
                                )}
                                {readOnlySurveyorGroup && (
                                  <div className="flex-1 flex justify-center min-w-[200px] max-w-[280px]">
                                    {renderRoleCard(readOnlySurveyorGroup)}
                                  </div>
                                )}
                                {readOnlyClientGroup && (
                                  <div className="flex-1 flex justify-center min-w-[200px] max-w-[280px]">
                                    {renderRoleCard(readOnlyClientGroup)}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )
                })()}
              </div>
              )}
            </div>

            {/* Surveyor Assignment for ReadOnlySurveyor */}
            {formData.group_ids && groups.find(g => formData.group_ids.includes(g.id) && g.name === 'ReadOnlySurveyor') && (
              <div className="mb-6 mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="bi bi-person-badge text-purple-600"></i>
                  Surveyor Assignment *
                </h4>
                <p className="text-xs text-gray-600 mb-4">
                  Select which surveyor this ReadOnlySurveyor user should be assigned to.
                </p>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Surveyor *</label>
                  {surveyors.length === 0 ? (
                    <div className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-500">
                      Loading surveyors...
                    </div>
                  ) : (
                    <select
                      value={formData.assigned_surveyor || ''}
                      onChange={(e) => setFormData({...formData, assigned_surveyor: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    >
                      <option value="">Select a surveyor...</option>
                      {surveyors.map(surveyor => (
                        <option key={surveyor.code} value={surveyor.code}>
                          {surveyor.code} - {surveyor.first_name} {surveyor.last_name} ({surveyor.surveyor_type})
                        </option>
                      ))}
                    </select>
                  )}
                  <p className="text-xs text-gray-500 mt-1">This surveyor will be accessible to the ReadOnlySurveyor user</p>
                </div>
              </div>
            )}

            {/* Client Assignment for ReadOnlyClient */}
            {formData.group_ids && groups.find(g => formData.group_ids.includes(g.id) && g.name === 'ReadOnlyClient') && (
              <div className="mb-6 mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="bi bi-building text-blue-600"></i>
                  Client Assignment *
                </h4>
                <p className="text-xs text-gray-600 mb-4">
                  Select which clients this ReadOnlyClient user should be assigned to. Multiple clients can be selected.
                </p>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Clients *</label>
                  {clients.length === 0 ? (
                    <div className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-500">
                      Loading clients...
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2">
                      {clients.map(client => (
                        <label key={client.code} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            key={`checkbox-${client.code}`}
                            checked={Array.isArray(formData.assigned_clients) && formData.assigned_clients.includes(client.code)}
                            onChange={(e) => {
                              setFormData(prevFormData => {
                                const currentClients = Array.isArray(prevFormData.assigned_clients) ? prevFormData.assigned_clients : []
                                let newClients
                                if (e.target.checked) {
                                  newClients = [...currentClients, client.code]
                                } else {
                                  newClients = currentClients.filter(c => c !== client.code)
                                }
                                return {...prevFormData, assigned_clients: newClients}
                              })
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm">{client.code} - {client.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">These clients will be accessible to the ReadOnlyClient user</p>
                  {formData.assigned_clients && formData.assigned_clients.length > 0 && (
                    <div className="mt-2">
                      <span className="text-xs font-medium text-gray-700">Selected: {formData.assigned_clients.length} client(s)</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )
      case 'surveyors':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Code *</label>
              <input
                type="text"
                value={formData.code || ''}
                onChange={(e) => setFormData({...formData, code: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                required
                disabled={modalMode === 'edit'}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">First Name</label>
              <input
                type="text"
                value={formData.first_name || ''}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Last Name</label>
              <input
                type="text"
                value={formData.last_name || ''}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Phone</label>
              <input
                type="text"
                value={formData.phone || ''}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Surveyor Type</label>
              <select
                value={formData.surveyor_type || 'In-House'}
                onChange={(e) => setFormData({...formData, surveyor_type: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="In-House">In-House</option>
                <option value="Subcont">Subcont</option>
              </select>
            </div>
            
            {/* Group Management Section */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Surveyor Group</label>
              <div className="space-y-2">
                {/* Current Groups */}
                <div className="flex flex-wrap gap-2">
                  {surveyorGroupMembers
                    .filter(member => member && member.surveyor === formData.code)
                    .map(member => {
                      const group = surveyorGroups.find(g => g.id === member.group)
                      return group ? (
                        <span key={member.id} className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded-full flex items-center gap-1">
                          {group.name}
                          <button
                            type="button"
                            onClick={() => handleDelete(member.id, 'surveyor-group-member')}
                            className="ml-1 text-purple-600 hover:text-purple-800"
                            title="Remove from group"
                          >
                            <i className="bi bi-x text-xs"></i>
                          </button>
                        </span>
                      ) : null
                    })}
                </div>
                
                {/* Add to Group Dropdown */}
                <div className="flex gap-2">
                  <select
                    value={formData.newGroup || ''}
                    onChange={(e) => setFormData({...formData, newGroup: e.target.value})}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="">Add to group...</option>
                    {surveyorGroups
                      .filter(group => !surveyorGroupMembers.some(member => 
                        member && member.surveyor === formData.code && member.group === group.id
                      ))
                      .map(group => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                  </select>
                  <button
                    type="button"
                    onClick={async () => {
                      if (formData.newGroup) {
                        try {
                          setLoading(true)
                          const memberData = {
                            surveyor: formData.code,
                            group: formData.newGroup
                          }
                          await adminAPI.createSurveyorGroupMember(memberData)
                          // Reload data to update the display
                          await loadData('surveyors')
                          // Reset the newGroup field
                          setFormData(prev => ({ ...prev, newGroup: '' }))
                        } catch (err) {
                          console.error('Error adding to group:', err)
                          setError('Failed to add to group')
                        } finally {
                          setLoading(false)
                        }
                      }
                    }}
                    disabled={!formData.newGroup}
                    className="px-4 py-2 bg-purple-500 text-white text-sm rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add to Group
                  </button>
                </div>
              </div>
            </div>
            
            <div className="mb-4 flex items-center gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active !== false}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="mr-2"
                />
                Is Active
              </label>
            </div>
          </>
        )
      case 'surveyor-group':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Group Name *</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                rows={3}
              />
            </div>
            <div className="mb-4 flex items-center gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active !== false}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="mr-2"
                />
                Is Active
              </label>
            </div>
          </>
        )
      case 'surveyor-group-member':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Surveyor *</label>
              <select
                value={formData.surveyor || ''}
                onChange={(e) => setFormData({...formData, surveyor: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                required
              >
                <option value="">Select Surveyor</option>
                {surveyors.map(surveyor => (
                  <option key={surveyor.code} value={surveyor.code}>
                    {surveyor.code} - {surveyor.first_name} {surveyor.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Group *</label>
              <select
                value={formData.group || ''}
                onChange={(e) => setFormData({...formData, group: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                required
              >
                <option value="">Select Group</option>
                {surveyorGroups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )
      case 'clients':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Client Code *</label>
              <input
                type="text"
                value={formData.code || ''}
                onChange={(e) => {
                  // Allow only letters and convert to uppercase
                  const value = e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase()
                  setFormData({...formData, code: value})
                }}
                maxLength={5}
                placeholder="5 letters (e.g., AARON)"
                className="w-full px-3 py-2 border rounded-lg"
                disabled={modalMode === 'edit'}
                title={modalMode === 'edit' ? 'Client code cannot be changed' : '5 letters only, no spaces or numbers'}
              />
              <p className="text-xs text-gray-500 mt-1">
                {modalMode === 'edit' ? 'Client code cannot be changed' : '5 letters only, no spaces or numbers'}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Email</label>
              <textarea
                value={formData.email || ''}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                rows="3"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Address</label>
              <textarea
                value={formData.address || ''}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                rows="3"
              />
            </div>
          </>
        )
      case 'countries':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
          </>
        )
      case 'ports':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Country *</label>
              <select
                value={formData.country || (typeof formData.country === 'object' ? formData.country?.id : formData.country) || ''}
                onChange={(e) => setFormData({...formData, country: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border rounded-lg"
                required
              >
                <option value="">Select Country</option>
                {countries.map(country => (
                  <option key={country.id} value={country.id}>{country.name}</option>
                ))}
              </select>
            </div>
          </>
        )
      case 'survey-types':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                required
                disabled={modalMode === 'edit'}
              />
            </div>
          </>
        )
      case 'operations':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Title *</label>
              <input
                type="text"
                value={formData.title || ''}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Start Date *</label>
              <input
                type="date"
                value={formData.start_date || ''}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">End Date *</label>
              <input
                type="date"
                value={formData.end_date || ''}
                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                rows="3"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Color</label>
              <select
                value={formData.color || 'orange'}
                onChange={(e) => setFormData({...formData, color: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="orange">Orange</option>
                <option value="red">Red</option>
                <option value="green">Green</option>
                <option value="blue">Blue</option>
                <option value="purple">Purple</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Survey Type</label>
              <input
                type="text"
                value={formData.survey_type || ''}
                onChange={(e) => setFormData({...formData, survey_type: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </>
        )
      case 'company-info':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Company Name *</label>
              <input
                type="text"
                value={formData.company_name || ''}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Address</label>
              <textarea
                value={formData.company_address || ''}
                onChange={(e) => setFormData({...formData, company_address: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg resize-y"
                placeholder="Enter company address"
                rows={3}
                style={{ minHeight: '80px' }}
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Phone</label>
              <input
                type="text"
                value={formData.company_tel || ''}
                onChange={(e) => setFormData({...formData, company_tel: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Enter phone number"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={formData.company_email || ''}
                onChange={(e) => setFormData({...formData, company_email: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Enter email address"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Web</label>
              <input
                type="text"
                value={formData.company_web || ''}
                onChange={(e) => setFormData({...formData, company_web: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Enter website URL"
              />
            </div>
          </>
        )
      case 'bank-info':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Header *</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            
            {/* Fixed Bank Fields */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Account Holder</label>
              <input
                type="text"
                value={formData.account_holder || ''}
                onChange={(e) => setFormData({...formData, account_holder: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Enter account holder name"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Bank Name</label>
              <input
                type="text"
                value={formData.bank_name || ''}
                onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Enter bank name"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Bank Address</label>
              <textarea
                value={formData.bank_address || ''}
                onChange={(e) => setFormData({...formData, bank_address: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg resize-y"
                placeholder="Enter bank address"
                rows={3}
                style={{ minHeight: '80px' }}
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">SWIFT</label>
              <input
                type="text"
                value={formData.swift || ''}
                onChange={(e) => setFormData({...formData, swift: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg font-mono"
                placeholder="Enter SWIFT code"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Bank / Branch Code</label>
              <input
                type="text"
                value={formData.bank_branch_code || ''}
                onChange={(e) => setFormData({...formData, bank_branch_code: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg font-mono"
                placeholder="Enter bank / branch code"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Account Number (SGD)</label>
              <input
                type="text"
                value={formData.account_number_sgd || ''}
                onChange={(e) => setFormData({...formData, account_number_sgd: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg font-mono"
                placeholder="Enter SGD account number"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Account Number (USD)</label>
              <input
                type="text"
                value={formData.account_number_usd || ''}
                onChange={(e) => setFormData({...formData, account_number_usd: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg font-mono"
                placeholder="Enter USD account number"
              />
            </div>
          </>
        )
      default:
        return null
    }
  }

  // Allow Admin users to access the page, but restrict Users tab to Superusers only
  if (!hasAdminAccess) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">You must be an Admin or Superuser to access this page.</p>
        </div>
      </div>
    )
  }


  const currentDataMap = {
    users,
    surveyors,
    clients,
    countries,
    ports,
    'survey-types': surveyTypes,
    surveys,
    operations,
    'company-info': companyInfo,
    'bank-info': bankInfo,
  }
  
  const currentData = (currentDataMap[activeTab] || [])
  // Ensure currentData is always an array
  const currentDataArray = Array.isArray(currentData) ? currentData : []

  return (
    <div className="page-container">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold"><span className="text-premium">JNJ Admin Panel</span></h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Manage users, roles and reference data</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-medium border border-gray-200 mb-6">
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-4 font-semibold transition-all duration-200 flex items-center gap-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-[#0a5fe0] border-b-2 border-[#0a5fe0] bg-[#f4f7fc]'
                  : 'text-gray-600 hover:text-[#0a5fe0] hover:bg-gray-50'
              }`}
            >
              <i className={`bi ${tab.icon}`}></i>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-medium border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">
            {tabs.find(t => t.id === activeTab)?.label} ({currentDataArray.length})
          </h2>
          {activeTab !== 'surveyors' && (
            <button
              onClick={handleCreate}
              className="px-4 py-2 text-sm font-semibold bg-premium text-white rounded-lg shadow-[0_2px_8px_rgba(1,109,247,0.3)] hover:shadow-[0_4px_14px_rgba(1,109,247,0.4)] transition-shadow flex items-center gap-2"
            >
              <i className="bi bi-plus-circle"></i>
              Add New
            </button>
          )}
        </div>

        {loading && !showModal && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#dbe7ff] border-t-[#0a5fe0]"></div>
          </div>
        )}
        
        {error && !showModal && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}
        
        {!loading && !showModal && !error && currentDataArray.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No {tabs.find(t => t.id === activeTab)?.label.toLowerCase()} found
          </div>
        )}
        
        {!loading && !showModal && !error && currentDataArray.length > 0 && (
          <div className="overflow-x-auto">
            {renderTable()}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className={`sticky top-0 px-6 py-4 flex justify-between items-center border-b ${
              modalType === 'surveyor-group' 
                ? 'bg-purple-50 border-purple-200' 
                : 'bg-white border-gray-200'
            }`}>
              <h3 className={`text-xl font-bold ${
                modalType === 'surveyor-group' 
                  ? 'text-purple-900' 
                  : 'text-gray-900'
              }`}>
                {(() => {
                  if (modalType === 'surveyor-group') {
                    return `${modalMode === 'create' ? 'Create' : 'Edit'} Surveyor Group`
                  } else if (modalType === 'surveyor-group-member') {
                    return `${modalMode === 'create' ? 'Add' : 'Edit'} Group Member`
                  } else if (modalType === 'surveyors') {
                    return `${modalMode === 'create' ? 'Create' : 'Edit'} Surveyor`
                  } else if (modalType === 'clients') {
                    return `${modalMode === 'create' ? 'Create' : 'Edit'} Client`
                  } else if (modalType === 'client-group') {
                    return `${modalMode === 'create' ? 'Create' : 'Edit'} Client Group`
                  } else if (modalType === 'client-group-member') {
                    return `${modalMode === 'create' ? 'Add' : 'Edit'} Client Group Member`
                  } else {
                    return `${modalMode === 'create' ? 'Create' : 'Edit'} ${tabs.find(t => t.id === activeTab)?.label}`
                  }
                })()}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false)
                  setModalType('')
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="bi bi-x-lg text-2xl"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-4">
                  {typeof error === 'object' ? JSON.stringify(error) : error}
                </div>
              )}
              {renderForm()}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                  setShowModal(false)
                  setModalType('')
                }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 rounded-lg font-semibold bg-premium text-white shadow-[0_2px_8px_rgba(1,109,247,0.3)] hover:shadow-[0_4px_14px_rgba(1,109,247,0.4)] transition-shadow disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Admin

